import { trackGoal } from '@lunary/ui-web/analytics'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { AuthUserResponseDto } from '@lunary/api-client'
import { api, setAccessToken, ApiError } from '../api/client'

type AuthState = {
  user: AuthUserResponseDto | null
  status: 'loading' | 'guest' | 'authenticated'
}

type AuthContextValue = AuthState & {
  guest: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName?: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  adoptSession: (accessToken: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function establishSession(kind: 'login' | 'register', body: Record<string, unknown>): Promise<void> {
  const res = await api.post<{ access_token: string; user: AuthUserResponseDto }>(`/auth/${kind}`, body)
  setAccessToken(res.access_token)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUserResponseDto | null>(null)
  const [status, setStatus] = useState<AuthState['status']>('loading')

  const refreshUser = useCallback(async () => {
    const me = await api.get<AuthUserResponseDto>('/auth/me')
    setUser(me)
  }, [])

  useEffect(() => {
    let cancelled = false
    const restore = async () => {
      try {
        const res = await api.post<{ access_token: string; user: AuthUserResponseDto }>(
          '/auth/refresh',
          undefined,
        )
        if (cancelled) return
        setAccessToken(res.access_token)
        setUser(res.user)
        setStatus('authenticated')
      } catch {
        if (!cancelled) setStatus('guest')
      }
    }
    restore()
    return () => {
      cancelled = true
    }
  }, [])

  const guest = useCallback(() => {
    setAccessToken(null)
    setUser(null)
    setStatus('guest')
    return Promise.resolve()
  }, [])

  const login = useCallback(
    async (email: string, password: string) => {
      await establishSession('login', { email, password })
      await refreshUser()
      setStatus('authenticated')
      trackGoal('login', { method: 'email' })
    },
    [refreshUser],
  )

  const register = useCallback(
    async (email: string, password: string, displayName?: string) => {
      await establishSession('register', { email, password, displayName })
      await refreshUser()
      setStatus('authenticated')
      trackGoal('sign_up', { method: 'email' })
    },
    [refreshUser],
  )

  const adoptSession = useCallback(
    async (accessToken: string) => {
      setAccessToken(accessToken)
      await refreshUser()
      setStatus('authenticated')
    },
    [refreshUser],
  )

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined)
    } catch {
      // refresh cookie может уже быть невалиден — сбрасываем локально
    }
    setAccessToken(null)
    setUser(null)
    setStatus('guest')
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, guest, login, register, logout, refreshUser, adoptSession }),
    [user, status, guest, login, register, logout, refreshUser, adoptSession],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export { ApiError }
